import React from 'react';
import { TrendingUp, Database, Lock, Clock } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { DataUploader } from '../components/analytics/DataUploader';
import { SpotifySourceCard } from '../components/analytics/sources/SpotifySourceCard';
import { HypedditSourceCard } from '../components/analytics/sources/HypedditSourceCard';
import { FilePreview } from '../components/analytics/FilePreview';
import { PreviewSummary } from '../components/analytics/PreviewSummary';
import { UploadStatus } from '../components/analytics/UploadStatus';
import { uploadProcessorService } from '../../backend/services/uploadProcessor';
import type { UploadProgress, UploadRequest, UploadResponse } from '../../shared/types/analytics';
import { DataGridPreview } from '../components/analytics/DataGridPreview';
import { supabase } from '../../backend/services/supabase';
import { deduplicationService } from '../../backend/services/deduplication';

export default function DataUploads() {
  const [preval, setPreval] = React.useState<any | null>(null);
  const [progress, setProgress] = React.useState<UploadProgress | null>(null);
  const [ingesting, setIngesting] = React.useState(false);
  const [ingestResp, setIngestResp] = React.useState<UploadResponse | null>(null);
  const [leftCollapsed, setLeftCollapsed] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'preview' | 'issues' | 'advanced'>('preview');

  const datasetType: 'songs' | 'playlists' | 'timeline' | null = React.useMemo(() => {
    return preval?.format || null;
  }, [preval?.format]);

  const canIngest = React.useMemo(() => !!preval && preval.valid, [preval]);

  const [gridConfig, setGridConfig] = React.useState<{ include: Record<string, boolean>; types: Record<string, any>; excludedRowIndices: number[] } | null>(null);
  const [showExplain, setShowExplain] = React.useState(false);
  const [bannerDismissed, setBannerDismissed] = React.useState(false);
  const [detectedPeriod, setDetectedPeriod] = React.useState<string>('');
  const [duplicateInfo, setDuplicateInfo] = React.useState<{ id: string; created_at: string; new_records: number; duplicates_found: number } | null>(null);
  const [possibleDataDuplicate, setPossibleDataDuplicate] = React.useState(false);
  const [showDupDetails, setShowDupDetails] = React.useState(false);

  React.useEffect(() => {
    // Reset banner when a new file is uploaded
    setBannerDismissed(false);
  }, [preval?.fileName, preval?.fileSize]);

  // Detect period from filename
  React.useEffect(() => {
    if (!preval?.file?.name) { setDetectedPeriod(''); return; }
    const name = preval.file.name.toLowerCase();
    if (/(^|[^a-z])(1day|1-day|daily)([^a-z]|$)/.test(name)) setDetectedPeriod('1 day');
    else if (/(^|[^a-z])(7day|7-day|weekly)([^a-z]|$)/.test(name)) setDetectedPeriod('7 days');
    else if (/(^|[^a-z])(28day|28-day|monthly)([^a-z]|$)/.test(name)) setDetectedPeriod('28 days');
    else setDetectedPeriod('');
  }, [preval?.file?.name]);

  // Duplicate hint by filename + size
  React.useEffect(() => {
    (async () => {
      setDuplicateInfo(null);
      const f = preval?.file;
      if (!f) return;
      try {
        const { data } = await supabase
          .from('data_uploads')
          .select('id,created_at,new_records,duplicates_found,filename,file_size')
          .eq('filename', f.name)
          .eq('file_size', f.size)
          .eq('source', (preval as any)?.source || 'spotify')
          .order('created_at', { ascending: false })
          .limit(1);
        if (data && data.length > 0) {
          setDuplicateInfo({ id: data[0].id, created_at: data[0].created_at, new_records: data[0].new_records || 0, duplicates_found: data[0].duplicates_found || 0 });
        }
      } catch {}
    })();
  }, [preval?.file]);

  // Data-level duplicate hint for a few records using fingerprints
  React.useEffect(() => {
    (async () => {
      setPossibleDataDuplicate(false);
      if (!preval?.parsed) return;
      try {
        const sample: any[] = (preval.parsed.songs?.slice(0, 3) || []).concat(preval.parsed.playlists?.slice(0, 2) || []);
        if (sample.length === 0) return;
        const hashes: string[] = sample.map((rec: any) => {
          const kind = rec.song_name ? 'song' : (rec.playlist_name ? 'playlist' : 'song');
          return deduplicationService.generateDataHash(rec, 'spotify', kind);
        });
        const { data } = await supabase
          .from('data_fingerprints')
          .select('id')
          .eq('source', (preval as any)?.source || 'spotify')
          .eq('upload_type', (preval as any)?.source === 'hypeddit' ? 'html' : 'csv')
          .in('data_hash', hashes);
        if (data && data.length > 0) setPossibleDataDuplicate(true);
      } catch {}
    })();
  }, [preval?.parsed]);

  const handlePrevalidation = (result: any) => {
    setPreval(result);
    setActiveTab('preview');
    setLeftCollapsed(true);
  };

  const handleIngest = async () => {
    if (!preval?.file) return;
    setIngesting(true);
    const clientId = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    uploadProcessorService.onProgress(clientId, (p) => setProgress(p));
    try {
      const req: UploadRequest = {
        file: preval.file,
        source: (preval as any)?.source || 'spotify',
        client_upload_id: clientId,
        override_period: ((datasetType === 'songs' || datasetType === 'playlists') && (preval as any)?.periodSuggestion) ? {
          dataset: datasetType as 'songs' | 'playlists',
          end_date: (preval as any).periodSuggestion.end,
          days: (preval as any).periodSuggestion.days
        } : undefined
      } as UploadRequest;
      const resp = await uploadProcessorService.processUpload(req);
      setIngestResp(resp);
      if (resp.success) {
        setProgress({ stage: 'completed', progress: 100, message: 'Upload completed' });
      } else {
        setProgress({ stage: 'error', progress: 0, message: resp.message, error_details: resp.message });
      }
    } catch (e: any) {
      setProgress({ stage: 'error', progress: 0, message: e?.message || 'Upload failed', error_details: String(e) });
    } finally {
      setIngesting(false);
      try { (uploadProcessorService as any).offProgress?.(clientId); } catch {}
    }
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          icon={<TrendingUp className="h-8 w-8" />}
          title="Data Uploads"
        />

        <div className="space-y-4">
          {/* Sticky header with three equal upload cards */}
          <div className="sticky top-16 z-20 bg-white/80 dark:bg-gray-900/80 backdrop-blur border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Spotify */}
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <img src="/brand/spotify_logo.svg" alt="Spotify" className="w-4 h-4" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Spotify for Artists</span>
                  <span className="text-xs rounded border px-2 py-0.5 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700">[CSV]</span>
                </div>
                <SpotifySourceCard onPrevalidated={handlePrevalidation} compact />
              </div>
              {/* Hypeddit */}
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <img src="/brand/hypeddit_logo.png" alt="Hypeddit" className="w-4 h-4" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Hypeddit</span>
                  <span className="text-xs rounded border px-2 py-0.5 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700">[HTML]</span>
                </div>
                <HypedditSourceCard onPrevalidated={handlePrevalidation} compact />
              </div>
              {/* Meta Coming Soon */}
              <div className="opacity-60">
                <div className="mb-1 flex items-center gap-2">
                  <img src="/brand/meta_logo.svg" alt="Meta" className="w-4 h-4" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Meta</span>
                  <span className="text-xs rounded border px-2 py-0.5 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700">[CSV]</span>
                </div>
                <div className="p-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 text-xs text-gray-500 text-center">
                  Coming soon
                </div>
              </div>
            </div>
          </div>

          {/* Right: Main Work Surface */}
          <section className="space-y-4">
            {leftCollapsed && (
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setLeftCollapsed(false)}
                  className="text-sm underline text-gray-700 dark:text-gray-300"
                >
                  Change source / Add files
                </button>
              </div>
            )}
            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Preview & Validation</h3>
                {preval && (
                  <div className="flex items-center gap-2 text-xs">
                    {datasetType && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                        Detected: {datasetType}
                      </span>
                    )}
                    {detectedPeriod && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                        {detectedPeriod}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="mt-3 border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex gap-6 text-sm">
                  {['preview','issues','advanced'].map(k => (
                    <button
                      key={k}
                      onClick={() => setActiveTab(k as any)}
                      className={`pb-2 ${activeTab===k ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
                    >
                      {k[0].toUpperCase()+k.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>
              {preval && !bannerDismissed && (preval.skippedCount > 0 || (preval.repairCount && preval.repairCount > 0)) && (
                <div className={`${preval.skippedCount > 0 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'} mt-3 border rounded-md p-3`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm text-gray-800 dark:text-gray-200">
                      {preval.skippedCount > 0 ? (
                        <>
                          We couldn’t read {preval.skippedCount} {preval.skippedCount === 1 ? 'row' : 'rows'}, so we skipped {preval.skippedCount === 1 ? 'it' : 'them'}. The rest looks good.
                        </>
                      ) : (
                        <>
                          We fixed a small formatting hiccup so your file could be read.
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setShowExplain(true)} className="text-sm underline text-gray-700 dark:text-gray-300">What happened?</button>
                      <button aria-label="Dismiss" onClick={() => setBannerDismissed(true)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">×</button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab==='issues' && (duplicateInfo || possibleDataDuplicate) && (
                <div className="mt-3 border rounded-md p-3 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-800 dark:text-gray-200">
                      {duplicateInfo ? 'Looks like you imported this file before.' : 'This looks similar to data you’ve imported before.'}
                    </div>
                    <button onClick={() => setShowDupDetails(v => !v)} className="text-sm underline text-gray-700 dark:text-gray-300">
                      {showDupDetails ? 'Hide details' : 'View last import'}
                    </button>
                  </div>
                  {showDupDetails && duplicateInfo && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      Imported on {new Date(duplicateInfo.created_at).toLocaleString()} • {duplicateInfo.new_records} new • {duplicateInfo.duplicates_found} duplicates
                    </div>
                  )}
                </div>
              )}
              {!preval && activeTab==='preview' && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Drop a Spotify CSV on the left to see a preview here.</p>
              )}
              {activeTab==='preview' && preval && (
                <div className="mt-3">
                  <PreviewSummary
                    format={datasetType}
                    records={preval.recordsCount || ((preval.parsed?.songs?.length || 0) + (preval.parsed?.playlists?.length || 0))}
                    dateRange={preval.periodSuggestion ? { start: preval.periodSuggestion.start, end: preval.periodSuggestion.end } : preval.parsed?.date_range}
                    fields={datasetType === 'songs' ? ['Song','Listeners','Streams','Saves','Release date'] : datasetType === 'playlists' ? ['Title','Author','Listeners','Streams','Date added'] : ['Date','Streams']}
                    repaired={preval.repairCount ? { count: preval.repairCount, lines: preval.repairedLines } : undefined}
                    skipped={preval.skippedCount}
                    sample={[...(preval.parsed?.songs || []).slice(0, 5), ...(preval.parsed?.playlists || []).slice(0, 5), ...((preval.parsed as any)?.campaigns || [])].slice(0,5).map((r: any) => {
                      if (r?.song_name) return `${r.song_name} — Streams: ${r.streams ?? 0}, Listeners: ${r.listeners ?? 0}`;
                      if (r?.playlist_name) return `${r.playlist_name} — Streams: ${r.streams ?? 0}, Listeners: ${r.listeners ?? 0}, Date added: ${r.recorded_at ?? ''}`;
                      if (r?.recorded_at && r?.streams !== undefined) return `${r.recorded_at} — Streams: ${r.streams}`;
                      return JSON.stringify(r);
                    })}
                    onDownloadCleaned={preval.cleanedCsv ? (() => {
                      const blob = new Blob([preval.cleanedCsv], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = preval.fileName || 'cleaned.csv';
                      a.click();
                      URL.revokeObjectURL(url);
                    }) : undefined}
                  />
                  {((datasetType === 'songs' || datasetType === 'playlists') && preval.periodSuggestion) || (datasetType === 'timeline' && (preval as any).periodSuggestion) ? (
                    <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                      {datasetType === 'timeline' ? 'Release date:' : `This file looks like ${preval.periodSuggestion.days}-day data ending on`}
                      <input
                        type="date"
                        defaultValue={(datasetType === 'timeline' ? (preval as any).periodSuggestion.release : preval.periodSuggestion.end) as string}
                        onChange={(e) => {
                          const end = e.target.value;
                          if (datasetType === 'timeline') {
                            const rel = new Date(end);
                            const cutoff = new Date(rel);
                            cutoff.setDate(cutoff.getDate() - 1);
                            const cutoffStr = cutoff.toISOString().split('T')[0];
                            setPreval({ ...preval, periodSuggestion: { ...((preval as any).periodSuggestion||{}), release: end, start: cutoffStr, end: ((preval as any).periodSuggestion?.end)||((preval as any).periodSuggestion?.end) } });
                          } else {
                            const d = new Date(end);
                            d.setDate(d.getDate() - (preval.periodSuggestion!.days - 1));
                            setPreval({ ...preval, periodSuggestion: { ...preval.periodSuggestion!, end, start: d.toISOString().split('T')[0] } });
                          }
                        }}
                        className="ml-2 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      />
                      <span className="ml-2 text-xs text-gray-500">Start: {(preval as any).periodSuggestion?.start}</span>
                    </div>
                  ) : null}
                </div>
              )}
              {activeTab==='issues' && preval && !preval.valid && preval.errors.length > 0 && (
                <div className="mt-3 text-sm text-red-600 dark:text-red-400">Fix issues before continuing.</div>
              )}
              {activeTab==='advanced' && (
                <div className="mt-4">
                  {preval?.headers && preval?.rows ? (
                    <DataGridPreview
                      headers={preval.headers}
                      rows={(datasetType === 'timeline' && (preval as any).periodSuggestion) ?
                        (preval.rows || []).filter((r: any) => (r.date || r.Date) >= (preval as any).periodSuggestion.start) : (preval.rows || [])}
                      defaultTypes={{ date: 'date', streams: 'number' }}
                      onConfigChange={setGridConfig}
                    />
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">Upload a CSV to configure advanced options.</p>
                  )}
                </div>
              )}
              {/* Sticky action bar */}
              <div className="sticky bottom-0 pt-2">
                <div className="flex items-center justify-end gap-2">
                  {preval && (
                    <button
                      onClick={() => { setPreval(null); setProgress(null); setIngestResp(null); setLeftCollapsed(false); setActiveTab('preview'); }}
                      className="text-sm px-3 py-2 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      Remove file
                    </button>
                  )}
                  <button
                    disabled={!canIngest || ingesting}
                    className={`text-sm px-3 py-2 rounded ${canIngest ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}
                    onClick={handleIngest}
                  >
                    {ingesting ? 'Importing…' : 'Import Data'}
                  </button>
                </div>
              </div>

              {/* Mapping removed for Spotify simple flow */}
            </div>

            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Ingestion Progress</h3>
              {progress ? (
                <UploadStatus progress={progress} />
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No active ingestion.</p>
              )}
            </div>

            {showExplain && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/40" onClick={() => setShowExplain(false)} />
                <div className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-5 w-full max-w-md mx-4">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Why we sometimes fix CSVs</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Some song titles include quotation marks — like 5' 4". In a few files, those quotes are saved in a way that confuses importers. We lightly tidy the file so it can be read, without changing your titles. If a line can’t be repaired, we skip just that one and keep everything else.
                  </p>
                  {preval?.repairedLines && preval.repairedLines.length > 0 && (
                    <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                      Fixed formatting on line{preval.repairedLines.length > 1 ? 's' : ''}: {preval.repairedLines.join(', ')}
                    </div>
                  )}
                  <div className="mt-4 flex justify-end">
                    <button onClick={() => setShowExplain(false)} className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700">Got it</button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
